import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface PersonalPhoto {
  id: string
  seq: number
  smallURL: string
  largeURL: string
  videoURL?: string
  mediaType: 0 | 1 // 0=照片 1=视频
  time: number // 拍摄时间戳（毫秒）
  addr?: string
  watermarkName?: string
  watermarkID?: string
  photoNumber?: string
}

export interface WatermarkOption {
  id: string
  name: string
}

export interface PersonalSpaceFilter {
  keyword: string
  startTime?: number
  endTime?: number
  watermarkIds: string[]
}

interface PersonalSpaceState {
  photos: PersonalPhoto[]
  total: number
  page: number
  pageSize: number
  loading: boolean
  filter: PersonalSpaceFilter
  watermarks: WatermarkOption[]
  selectedIds: string[]
  isSelectAll: boolean
  isPersonalVip: boolean
  vipStatusLoaded: boolean
  previewIndex: number // -1 表示不预览
  isSearchMode: boolean // true=搜索模式，false=全量模式
  lastPageCond: string // 搜索游标，空串表示没有更多
  isSpaceOpen: boolean // 是否开通个人空间（来自 /web/user/space/info）
}

const initialState: PersonalSpaceState = {
  photos: [],
  total: 0,
  page: 1,
  pageSize: 60,
  loading: false,
  filter: { keyword: '', watermarkIds: [] },
  watermarks: [],
  selectedIds: [],
  isSelectAll: false,
  isPersonalVip: false,
  vipStatusLoaded: false,
  previewIndex: -1,
  isSearchMode: false,
  lastPageCond: '',
  isSpaceOpen: false
}

const personalSpaceSlice = createSlice({
  name: 'mySpace/personalSpace',
  initialState,
  reducers: {
    setPhotos(
      state,
      action: PayloadAction<{ list: PersonalPhoto[]; total: number }>
    ) {
      state.photos = action.payload.list
      state.total = action.payload.total
    },
    setPage(state, action: PayloadAction<number>) {
      state.page = action.payload
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload
    },
    setFilter(state, action: PayloadAction<Partial<PersonalSpaceFilter>>) {
      Object.assign(state.filter, action.payload)
      state.page = 1
    },
    resetFilter(state) {
      state.filter = { keyword: '', watermarkIds: [] }
      state.page = 1
    },
    setWatermarks(state, action: PayloadAction<WatermarkOption[]>) {
      state.watermarks = action.payload
    },
    toggleSelectPhoto(state, action: PayloadAction<string>) {
      const id = action.payload
      const idx = state.selectedIds.indexOf(id)
      if (idx === -1) {
        state.selectedIds.push(id)
      } else {
        state.selectedIds.splice(idx, 1)
      }
      state.isSelectAll =
        state.selectedIds.length === state.photos.length &&
        state.photos.length > 0
    },
    toggleSelectAll(state) {
      if (state.isSelectAll) {
        state.selectedIds = []
        state.isSelectAll = false
      } else {
        state.selectedIds = state.photos.map((p) => p.id)
        state.isSelectAll = true
      }
    },
    clearSelection(state) {
      state.selectedIds = []
      state.isSelectAll = false
    },
    setPersonalVip(
      state,
      action: PayloadAction<{ isVip: boolean; loaded: boolean }>
    ) {
      state.isPersonalVip = action.payload.isVip
      state.vipStatusLoaded = action.payload.loaded
    },
    setPreviewIndex(state, action: PayloadAction<number>) {
      state.previewIndex = action.payload
    },
    appendPhotos(
      state,
      action: PayloadAction<{
        list: PersonalPhoto[]
        total: number
        lastPageCond: string
      }>
    ) {
      state.photos = [...state.photos, ...action.payload.list]
      state.total = action.payload.total
      state.lastPageCond = action.payload.lastPageCond
    },
    setSearchMode(state, action: PayloadAction<boolean>) {
      state.isSearchMode = action.payload
    },
    setLastPageCond(state, action: PayloadAction<string>) {
      state.lastPageCond = action.payload
    },
    setSpaceOpen(state, action: PayloadAction<boolean>) {
      state.isSpaceOpen = action.payload
    },
    removePhotos(state, action: PayloadAction<string[]>) {
      const ids = new Set(action.payload)
      state.photos = state.photos
        .filter((p) => !ids.has(p.id))
        .map((p, i) => ({ ...p, seq: i + 1 }))
      state.total = Math.max(0, state.total - action.payload.length)
    }
  }
})

export const {
  setPhotos,
  setPage,
  setLoading,
  setFilter,
  resetFilter,
  setWatermarks,
  toggleSelectPhoto,
  toggleSelectAll,
  clearSelection,
  setPersonalVip,
  setPreviewIndex,
  appendPhotos,
  setSearchMode,
  setLastPageCond,
  setSpaceOpen,
  removePhotos
} = personalSpaceSlice.actions

export default personalSpaceSlice.reducer
